import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  useBloodPressureCalculations,
  useBloodPressureChartConfig,
  useBloodPressureChartData,
  useBloodPressureData,
} from "~/hooks";
import { BloodPressureBreakdown } from "./BloodPressureBreakdown";
import { BloodPressureChart } from "./BloodPressureChart";
import { BloodPressureSummary } from "./BloodPressureSummary";
import { TimeRangeSelector } from "../Shared";

type TimeRange = "1d" | "7d" | "1m" | "6m" | "1y";

interface BloodPressureDetailViewProps {
  patientId: number;
  patientName: string;
}

export const BloodPressureDetailView: React.FC<
  BloodPressureDetailViewProps
> = ({ patientId, patientName }) => {
  const navigate = useNavigate();
  const [selectedRange, setSelectedRange] = useState<TimeRange>("7d");
  const [navigationOffset, setNavigationOffset] = useState(0);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  // Fetch blood pressure data from API
  const {
    data: apiData,
    metadata,
    loading,
    error,
  } = useBloodPressureData({
    patientId,
    timeRange: selectedRange,
    navigationOffset,
    selectedDate,
  });

  // Transform API data to chart format using custom hook
  const { filteredData, systolicChartData, diastolicChartData } =
    useBloodPressureChartData({
      apiData,
      selectedRange,
    });

  // Get chart configuration using custom hook
  const chartConfig = useBloodPressureChartConfig({
    selectedRange,
    navigationOffset,
    selectedDate,
    systolicChartData,
    diastolicChartData,
  });

  // Use the calculations hook
  const { breakdownTitle } = useBloodPressureCalculations({
    filteredData,
    timeRange: selectedRange,
    navigationOffset,
  });

  // Navigation functions
  const handlePrevious = () => {
    switch (selectedRange) {
      case "1d": {
        const prevDay = new Date(selectedDate);
        prevDay.setDate(selectedDate.getDate() - 1);
        setSelectedDate(prevDay);
        break;
      }
      case "7d": {
        const prevWeek = new Date(selectedDate);
        prevWeek.setDate(selectedDate.getDate() - 7);
        setSelectedDate(prevWeek);
        break;
      }
      case "1m": {
        const prevMonth = new Date(selectedDate);
        prevMonth.setMonth(selectedDate.getMonth() - 1);
        setSelectedDate(prevMonth);
        break;
      }
      case "6m": {
        const prev6Month = new Date(selectedDate);
        prev6Month.setMonth(selectedDate.getMonth() - 6);
        setSelectedDate(prev6Month);
        break;
      }
      case "1y": {
        const prevYear = new Date(selectedDate);
        prevYear.setFullYear(selectedDate.getFullYear() - 1);
        setSelectedDate(prevYear);
        break;
      }
    }
  };

  const handleNext = () => {
    switch (selectedRange) {
      case "1d": {
        const nextDay = new Date(selectedDate);
        nextDay.setDate(selectedDate.getDate() + 1);
        setSelectedDate(nextDay);
        break;
      }
      case "7d": {
        const nextWeek = new Date(selectedDate);
        nextWeek.setDate(selectedDate.getDate() + 7);
        setSelectedDate(nextWeek);
        break;
      }
      case "1m": {
        const nextMonth = new Date(selectedDate);
        nextMonth.setMonth(selectedDate.getMonth() + 1);
        setSelectedDate(nextMonth);
        break;
      }
      case "6m": {
        const next6Month = new Date(selectedDate);
        next6Month.setMonth(selectedDate.getMonth() + 6);
        setSelectedDate(next6Month);
        break;
      }
      case "1y": {
        const nextYear = new Date(selectedDate);
        nextYear.setFullYear(selectedDate.getFullYear() + 1);
        setSelectedDate(nextYear);
        break;
      }
    }
  };

  // Handle time range change and preserve current view
  const handleTimeRangeChange = (newRange: TimeRange) => {
    setSelectedRange(newRange);
    setNavigationOffset(0); // Reset navigation offset
  };

  // Date selection handler
  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    // Reset navigation offset since we're now using selectedDate directly
    setNavigationOffset(0);
  };

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between border-b border-gray-700 pb-4">
        <button
          onClick={() => navigate(-1)}
          className="text-lg font-medium text-blue-400 transition-colors hover:text-blue-300"
        >
          ← Back
        </button>
        <h1 className="text-2xl font-semibold text-white">
          Blood Pressure - {patientName}
        </h1>
        <div className="w-16"></div> {/* Spacer for centering */}
      </div>

      {/* Content */}
      <div className="space-y-6">
        {/* Time Range Selector */}
        <TimeRangeSelector
          selectedRange={selectedRange}
          setSelectedRange={handleTimeRangeChange}
        />

        {/* Summary Statistics */}
        <BloodPressureSummary
          filteredData={filteredData}
          metadata={metadata}
          handlePrevious={handlePrevious}
          handleNext={handleNext}
          onDateSelect={handleDateSelect}
          selectedDate={selectedDate}
          selectedRange={selectedRange}
        />

        {/* Chart */}
        <div className="mx-auto w-4/5 rounded-xl bg-gray-900/50 p-4">
          <BloodPressureChart
            key={`${selectedRange}-${selectedDate.getTime()}-${filteredData.length}`}
            selectedRange={selectedRange}
            navigationOffset={navigationOffset}
            selectedDate={selectedDate}
            filteredData={filteredData}
            systolicChartData={systolicChartData}
            diastolicChartData={diastolicChartData}
            loading={loading}
            error={error}
            chartConfig={chartConfig}
          />
        </div>

        {/* Breakdown List */}
        <BloodPressureBreakdown
          selectedRange={selectedRange}
          filteredData={filteredData}
          breakdownTitle={breakdownTitle}
          loading={loading}
          error={error}
        />
      </div>
    </div>
  );
};
